'use client'

import { useMemo, useRef, useState } from 'react'

import { DatePicker } from '@/app/components/shared/date/DatePicker'
import {
  getMaxDate,
  getToday,
  parseIsoDate,
  toIsoDate,
  type DateRange,
} from '@/app/components/shared/date/Calendar/utils/dateRange'
import { VerticalCard } from '@/app/components/shared/cards/VerticalCard'
import { toModuleAnalyticsItems } from '@/app/components/modules/analytics/buildModuleListAnalytics'
import {
  AGENDA_LIST_NAME,
  AGENDA_MODULE_NAME,
  AGENDA_MODULE_TYPE,
} from '@/app/components/modules/analytics/constants'
import { TrackedProductGrid } from '@/app/components/shared/TrackedProductGrid'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NOT_SET } from '@/app/lib/analytics/constants'
import { ALL_LOCATIONS } from '@/app/lib/constants'
import { ProgressCircleIndeterminate } from '@/app/components/atoms/ProgressCircleIndeterminate'
import { toProductCards } from '@/app/lib/api/mapProductToCard'
import type { ProductApiItem } from '@/app/lib/api/types-api'

import { fetchAgendaModuleDayAction } from './actions'
import { EMPTY_STATE_TITLE, LOADING_LABEL } from './AgendaClient.constants'
import { filterVisibleEvents } from './filterVisibleEvents'
import styles from './AgendaClient.module.scss'
import type { AgendaClientProps } from './AgendaClient.types'

/** Clave estable de un `DateRange` para descartar peticiones obsoletas — dos
 * rangos con las mismas fechas producen la misma clave aunque sean objetos
 * `Date` distintos. */
function rangeKey(range: DateRange): string {
  return range.end ? `${toIsoDate(range.start)}_${toIsoDate(range.end)}` : toIsoDate(range.start)
}

/**
 * `AgendaClient` — subárbol interactivo del módulo `Agenda`: selector de fecha
 * (`DatePicker`, componente compartido — sin filtros ni chips propios, Out of
 * Scope de spec.md) + rejilla de tarjetas verticales de la fecha/rango
 * vigente. Admite tanto una única fecha como un rango de dos (selección de
 * múltiples fechas al estilo reserva de vuelos, fiel al diseño de referencia
 * `eci-firefly-uikit`) — `DatePicker` resuelve el click con dos-clicks
 * (`pickDate`), este componente solo reacciona al `DateRange` ya resuelto.
 *
 * Rango seleccionable hoy → hoy+3 años, mismo criterio ya usado por `/agenda`
 * (`getMaxDate`) — sin introducir un límite propio del módulo (Assumptions de
 * spec.md).
 *
 * El día inicial y los eventos iniciales llegan del servidor ya resueltos
 * (`Agenda.tsx`): `initialDay` viaja como ISO string, nunca como `Date`
 * (Principio I — límite serializable servidor→cliente). El módulo siempre
 * arranca con una única fecha activa (hoy) — nunca con un rango ya aplicado,
 * igual que antes de esta ampliación.
 *
 * Cambio de fecha (Historia 2): dispara `fetchAgendaModuleDayAction` en
 * segundo plano, sin cambiar la URL ni recargar la página (FR-013). El
 * listado anterior permanece visible (con el spinner superpuesto) hasta que
 * la respuesta llega; una respuesta que quede desactualizada por una
 * selección más reciente se descarta comparando contra
 * `latestRequestedKeyRef` (research.md §7, FR-016). Un fallo de red mantiene
 * el listado anterior y oculta el spinner (FR-018) — la fecha seleccionada no
 * se revierte.
 *
 * La localización vigente (prop `location`, la de la URL de la página
 * anfitriona — ver `Agenda.tsx`; ya NO `useCityContext()`, que podía quedar
 * desincronizada de esa URL) viaja en CADA cambio de fecha: ambos filtros
 * (fecha + localización) se mantienen siempre juntos en la petición.
 */
export function AgendaClient({
  initialDay,
  initialEvents,
  location,
  modulePosition,
}: AgendaClientProps) {
  const pageAnalytics = useOptionalPageAnalytics()
  const [range, setRange] = useState<DateRange>(() => ({
    start: parseIsoDate(initialDay) ?? getToday(),
    end: null,
  }))
  const [events, setEvents] = useState<ProductApiItem[]>(initialEvents)
  const [isLoading, setIsLoading] = useState(false)
  // Se incrementa con cada respuesta aplicada: remonta la rejilla instrumentada
  // para que las impresiones del nuevo listado vuelvan a contarse (la
  // deduplicación vive en el hook, que solo se reinicia al desmontar).
  const [resultsVersion, setResultsVersion] = useState(0)

  // Última fecha/rango solicitado (no el último resuelto): permite descartar
  // una respuesta tardía de una selección que el visitante ya ha abandonado
  // (FR-016).
  const latestRequestedKeyRef = useRef(initialDay)

  const cards = useMemo(() => toProductCards(events), [events])

  const min = getToday()
  const max = getMaxDate(min)

  /** `YYYY-MM-DD[_YYYY-MM-DD]` del rango aplicado, o `''` si es el de por defecto (hoy). */
  function appliedDate(forRange: DateRange): string {
    const start = toIsoDate(forRange.start)
    const value = forRange.end ? `${start}_${toIsoDate(forRange.end)}` : start
    return value === toIsoDate(min) ? '' : value
  }

  function handleChange(nextRange: DateRange) {
    const key = rangeKey(nextRange)
    // Reelegir la fecha/rango ya vigente no dispara ninguna petición
    // adicional (Edge Cases de spec.md) — ni siquiera si aún hay una carga en
    // curso para esa misma selección, ya cubierta por la petición ya
    // lanzada.
    if (key === latestRequestedKeyRef.current) return

    // `filter_change` de fecha (`Listado filtrado.csv` → «Cambio de filtro»),
    // inmediato. Mismo criterio que `/agenda` y la Agenda de canal: hoy sin rango
    // es la selección implícita de la tira, no un filtro aplicado.
    const previousValue = appliedDate(range)
    const nextValue = appliedDate(nextRange)
    if (previousValue !== nextValue) {
      pageAnalytics?.trackFilterChange({
        filter_action: nextValue ? 'apply' : 'remove',
        filter_type: 'fecha',
        filter_value: nextValue || NOT_SET,
        filter_previous_value: previousValue || NOT_SET,
        filters_active_count: String((location !== ALL_LOCATIONS ? 1 : 0) + (nextValue ? 1 : 0)),
        module_name: AGENDA_MODULE_NAME,
        module_type: AGENDA_MODULE_TYPE,
      })
    }

    latestRequestedKeyRef.current = key
    setRange(nextRange)
    setIsLoading(true)

    const startAt = toIsoDate(nextRange.start)
    const endAt = nextRange.end ? toIsoDate(nextRange.end) : undefined

    fetchAgendaModuleDayAction(startAt, endAt, location)
      .then((freshEvents) => {
        // Respuesta obsoleta: el visitante ya seleccionó otra fecha mientras
        // esta petición estaba en curso — se descarta sin tocar el estado.
        if (latestRequestedKeyRef.current !== key) return
        setEvents(filterVisibleEvents(freshEvents))
        setResultsVersion((version) => version + 1)
        setIsLoading(false)
      })
      .catch((error: unknown) => {
        if (latestRequestedKeyRef.current !== key) return
        console.error('Error al cambiar de fecha en el módulo Agenda', error)
        setIsLoading(false)
      })
  }

  return (
    <div className={styles.root}>
      <DatePicker range={range} min={min} max={max} onChange={handleChange} variant="default" />

      {isLoading && (
        <div className={styles.statusRow}>
          <ProgressCircleIndeterminate size="m" label={LOADING_LABEL} />
        </div>
      )}

      {!isLoading && cards.length === 0 ? (
        <p className={styles.emptyState}>{EMPTY_STATE_TITLE}</p>
      ) : (
        // `view_item_list`/`select_item`/`add_to_cart` (`Listado filtrado.csv`); el
        // `item_list_id` sale del `page_type` vigente. La rejilla se sustituye con
        // cada respuesta (el `key` de las cards cambia), así que se vuelven a contar
        // las impresiones del nuevo día.
        <TrackedProductGrid
          key={resultsVersion}
          className={styles.grid}
          analytics={{
            itemListName: AGENDA_LIST_NAME,
            moduleName: AGENDA_MODULE_NAME,
            moduleType: AGENDA_MODULE_TYPE,
            modulePosition,
            items: toModuleAnalyticsItems(cards, events),
          }}
        >
          {cards.map(({ id, ...card }) => (
            <VerticalCard key={id} {...card} />
          ))}
        </TrackedProductGrid>
      )}
    </div>
  )
}
